import { Component, OnInit, ViewChild } from '@angular/core';
import { ConfirmationService, MenuItem, SelectItem } from 'primeng/api';
import { RevenueItemService } from 'src/app/revenue/shared/revenue-items/revenue-item.service';
import { DatagridService, DialogService, NotificationService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { EcomItemTypeSearchDto } from '../shared/dto/ecom-item-type-search.dto';
import { EcomItemCopyDto } from '../shared/dto/ecom-item-copy.dto';
import { EcomItemDto } from '../shared/dto/ecom-item.dto';
import { EcomSearch } from '../shared/dto/ecom-search.dto';
import { EcomItemTypeService } from '../shared/ecom-item-type.service';
import { EcomService } from '../shared/ecom.service';
import { EComItemSettingsDialogComponent } from '../ecom-item-settings-dialog/ecom-item-settings-dialog.component';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { Subject, Subscription } from 'rxjs';
import { EcomItemTypeDto } from '../shared/dto/ecom-item-type.dto';
import { EcomPriceOperatorEnum } from '../shared/dto/ecom-price-operator-enums.enum';
import { debounce } from 'lodash-decorators';
import { RevenueItem } from 'src/app/revenue/shared/revenue-items/revenue-item.model';

@Component({
  selector: 'wl-ecom-list',
  templateUrl: './ecom-list.component.html',
  styleUrls: ['./ecom-list.component.less'],
    standalone: false
})
export class EComListComponent extends LazySelectableTableComponent<EcomSearch, EcomItemDto> implements OnInit {
  @ViewChild(EComItemSettingsDialogComponent) private _editDialog: EComItemSettingsDialogComponent;
  rowMenuItems = new Map<number, MenuItem[]>();
  showEdit = false;
  ecomItemEditId = 0;
  ecomTypeSearchParams = {} as EcomItemTypeSearchDto;
  ecomItemTypes: EcomItemTypeDto[];
  revenueItemOpts: SelectItem[];
  ecomItemTypeOptions: SelectItem[];
  operatorOptions: SelectItem[] = [
    { value: EcomPriceOperatorEnum.GreaterThan, label: '>' },
    { value: EcomPriceOperatorEnum.EqualTo, label: '=' },
    { value: EcomPriceOperatorEnum.LessThan, label: '<' },
  ];
  revenueItems: Array<RevenueItem> = [];
  private _onDestroy$ = new Subject<void>();

  copyMessage: string = '';
  constructor(
    datagridService: DatagridService,
    private _translationService: TranslationService,
    private _ecomService: EcomService,
    private _notificationService: NotificationService,
    private _confirmationService: ConfirmationService,
    private _revenueItemService: RevenueItemService,
    private _ecomItemTypeService: EcomItemTypeService,
    private _dialogService: DialogService,
    private _navigationService: NavigationService
  ) {
    super('itemId', datagridService);
    this.searchParams = new EcomSearch();
  }

  async ngOnInit(): Promise<void> {
    const ecommerceTypeSearchResult = await this._ecomItemTypeService.search({ PageNumber: 0 } as EcomItemTypeSearchDto);
    this.ecomItemTypes = ecommerceTypeSearchResult.Result;

    this.ecomItemTypeOptions = this.ecomItemTypes.map((ecomItemType) => {
      return { value: ecomItemType.Ecom_ItemTypeID, label: ecomItemType.Ecom_ItemType };
    });

    const revenueResults = await this._revenueItemService.getAllRevenueItems();
    this.revenueItems = revenueResults;
    this.revenueItemOpts = revenueResults.map((x) => {
      return { value: x.ItemNum, label: x.ItemName };
    });

    this.search();
  }

  actionItemsForEcomItems(ecommerceItemId: number) {
    if (!this.rowMenuItems.has(ecommerceItemId)) {
      this.rowMenuItems.set(ecommerceItemId, [
        {
          label: this._translationService.getInstantTitlecase('common.edit'),
          command: () => {
            this._editDialog.loadItem(ecommerceItemId);
          },
        },
        {
          label: this._translationService.getInstantTitlecase('common.delete'),
          command: () => this.deleteEcomItem(ecommerceItemId),
        },
        {
          label: this._translationService.getInstantTitlecase('common.copy'),
          command: () => {
            this.copyEcomItem(ecommerceItemId);
          },
        },
      ]);
    }
    return this.rowMenuItems.get(ecommerceItemId);
  }

  fetchResults(): Promise<PagedResultGeneric<EcomItemDto>> {
    return this._ecomService.search(this.searchParams).then((results) => {
      results?.Result?.forEach((ecommerceItem) => {
        if (ecommerceItem?.ItemNum) {
          ecommerceItem.RevenueItem = this.revenueItems.find((x) => x.ItemNum === ecommerceItem.ItemNum)?.ItemName;
        }
        if (ecommerceItem?.ItemTypeId) {
          ecommerceItem.ItemType = this.ecomItemTypes.find((x) => x.Ecom_ItemTypeID === ecommerceItem.ItemTypeId).Ecom_ItemType;
        }
      });
      return results;
    });
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  toggleInactive(ecommerceItem: EcomItemDto) {
    this._ecomService.SaveItem(ecommerceItem).then(() => {
      this.search();
    });
  }

  addNewEcomItem() {
    this.ecomItemEditId = 0;
    this._editDialog.loadItem(this.ecomItemEditId);
  }

  deleteEcomItem(itemId: number) {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('ecommerce.ecommerce-list.confirm-delete-message'),
      key: 'deleteEcomItemCD',
      accept: () => {
        this.loading = true;
        this._ecomService
          .DeleteEcomItem(itemId)
          .then(() => {
            this._notificationService.translateSuccess('ecommerce.ecommerce-list.ecom-item-deleted-message');
            this.search();
          })
          .catch((error) => {
            this._notificationService.httpError(error);
          });
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  copyEcomItem(itemId: number) {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('ecommerce.item.copy'),
      key: 'copyEcomItemCD',
      accept: () => {
        this.copyItem(itemId);
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  copyItem(itemId: number) {
    this._dialogService.showWaitIndicator();
    var copyItem = {} as EcomItemCopyDto;
    copyItem.ItemId = itemId;
    copyItem.CopyItemAffCodes = true;
    copyItem.CopyItemInventories = true;
    copyItem.CopyItemOptions = true;
    copyItem.CopyItemShippingTypes = true;
    copyItem.CopyImages = true;
    this._ecomService.copyItem(copyItem).then(
      (newItemId) => {
        if (newItemId > 0) {
          this._notificationService.translateSuccess('ecommerce.ecommerce-list.ecom-item-copy-message');
          this.search();
          this._editDialog.loadItem(newItemId);
          this._dialogService.closeWaitIndicator();
        }
      },
      (error: any) => {
        this._dialogService.error(error);
      }
    );
  }

  navigateToEcomSpa() {
    this._navigationService.navigateToEcomSpa();
  }

  @debounce(500)
  searchHandler() {
    this.search();
  }

  itemIdSearch(itemId) {
    if (itemId) {
      this.searchParams.ItemIds = [itemId];
    } else {
      this.searchParams.ItemIds = null;
    }
    this.searchHandler();
  }
}
